/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect, useContext } from "react";
import { ContentZoneContext } from "cms/components/context";
import { SVGIcons } from "cms/icons/svg-icons";
import Config from "data/config";
import SimpliCityAdminSDK from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { EventCard } from "./event-card";
import { MeetingCard } from "./meeting-card";
import { transformWebpageToMeeting } from "./meeting-transformer";
import { transformWebpageToEvent } from "./event-transformer";
import { useFeatureFlag } from "cms/features/use-feature-flag";

const EventsCouncilFeedComponentDisplay = () => {
    const { token } = useAuth();
    const { component } = useContext(ContentZoneContext);
    const eventHeading = component?.eventHeading || "";
    const allEventsLink = component?.allEventsLink;
    const showEventDescriptions = component?.showEventDescriptions || false;
    const councilMeetingHeading = component?.councilMeetingHeading || "";
    const councilMeetingType = component?.councilMeetingType || "";
    const allCouncilMeetingsLink = component?.allCouncilMeetingsLink;

    const feature_use_new_event2_model_type = useFeatureFlag("feature_use_new_event2_model_type");

    const [events, setEvents] = useState([]);
    const [councilMeetings, setCouncilMeetings] = useState([]);

    useEffect(() => {
        const fetchEvents = async () => {
            if (!token) return;

            try {
                const webpages = await SimpliCityAdminSDK.webpage.advancedSearch(token, {
                    modelType: feature_use_new_event2_model_type ? "model.event2" : "model.event",
                    pageSize: 4,
                    pageLimit: 1,
                });

                // Fetch models for all webpages in parallel
                const webpagesWithModels = await Promise.all(
                    webpages.results.map(async (webpage) => {
                        const model = await SimpliCityAdminSDK.model.findOne(token, webpage.model);
                        return {
                            ...webpage,
                            modelObject: model,
                        };
                    })
                );

                // Transform webpages to event format
                const transformedEvents = webpagesWithModels.map((webpage) =>
                    transformWebpageToEvent(webpage)
                );

                setEvents(transformedEvents);
            } catch (error) {
                console.error("Error fetching events:", error);
                setEvents([]);
            }
        };

        fetchEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        const fetchCouncilMeetings = async () => {
            if (!token) return;

            try {
                const webpages = await SimpliCityAdminSDK.webpage.advancedSearch(token, {
                    modelType: "model.council-meeting",
                    pageSize: 1,
                    pageLimit: 1,
                    fields: [
                        {
                            name: "council_meeting_category",
                            value: councilMeetingType || "",
                        }
                    ]
                });

                // Fetch models for all webpages in parallel
                const webpagesWithModels = await Promise.all(
                    webpages.results.map(async (webpage) => {
                        const model = await SimpliCityAdminSDK.model.findOne(token, webpage.model);
                        return {
                            ...webpage,
                            modelObject: model,
                        };
                    })
                );

                // Transform webpages to meeting format
                const transformedMeetings = webpagesWithModels.map((webpage) =>
                    transformWebpageToMeeting(webpage)
                );

                setCouncilMeetings(transformedMeetings);
            } catch (error) {
                console.error("Error fetching council meetings:", error);
                setCouncilMeetings([]);
            }
        };

        fetchCouncilMeetings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, councilMeetingType]);
    return (
        <div className='simplicity-events-council-feed-component-display'>
            <div className="simplicity-events-council-feed-component-display-events-container">
                <div className="event-heading-container">
                    <div className="event-heading">
                        <h2>{eventHeading}</h2>
                    </div>
                    <a 
                        href={allEventsLink?.url ? `${Config.previewUrl}${allEventsLink.url}` : "#"} 
                        target={allEventsLink ? "_blank" : undefined}
                        rel={allEventsLink ? "noopener noreferrer" : undefined}
                        className="event-link"
                    >
                        <div className="event-link-text">{allEventsLink?.text || "All events"}</div> 
                        <div className="event-link-icon"><SVGIcons.common.RightArrowBlue /></div>
                    </a>
                </div>
                <div className="event-items">
                    {events.length > 0 ? events.map((event) => (
                        <EventCard key={event.id || event.guid} event={event} showDescription={showEventDescriptions} />
                    )) : (
                        <div className="event-items-placeholder">No events available.</div>
                    )}
                </div>
            </div>
            <div className="simplicity-events-council-feed-component-display-council-meeting-container-separator"></div>
            <div className="simplicity-events-council-feed-component-display-council-meeting-container">
                <div className="council-meeting-heading-container">
                    <div className="council-meeting-heading">
                        <h2>{councilMeetingHeading}</h2>
                    </div>
                    <a 
                        href={allCouncilMeetingsLink?.url ? `${Config.previewUrl}${allCouncilMeetingsLink.url}` : "#"} 
                        target={allCouncilMeetingsLink ? "_blank" : undefined}
                        rel={allCouncilMeetingsLink ? "noopener noreferrer" : undefined}
                        className="council-meeting-link"
                    >
                        <div className="council-meeting-link-text">{allCouncilMeetingsLink?.text || "All council meetings"}</div> 
                        <div className="council-meeting-link-icon"><SVGIcons.common.RightArrowBlue /></div>
                    </a>
                </div>
                <div className="council-meeting-items">
                    {councilMeetings.length > 0 ? councilMeetings.map((meeting) => (
                        <MeetingCard key={meeting.id || meeting.guid} meeting={meeting} />
                    )) : (
                        <div className="council-meeting-items-placeholder">No council meetings available.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

EventsCouncilFeedComponentDisplay.displayName = "EventsCouncilFeedComponentDisplay";
export { EventsCouncilFeedComponentDisplay };
